import React, { useContext, useMemo, useState } from "react";
import { Button, Dialog, IconButton, TextField } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useDropzone } from "react-dropzone";

import { Auth } from "lib/Auth";
import { DataTable } from "components/dataTable/DataTable";
import Toast, { IToastBasicProps } from "components/Toast/Toast";
import { Loading } from "components/loading/Loading";
import { FMSThemeContext } from "contexts/FMSThemeContext";
import { MaterialContext } from "contexts/MaterialsContext";

import { BusniessUnit, IBranch } from "types/Branch";
import { ICateringRecipe, CateringRecipeType } from "types/CateringRecipe";
import { IMaterial, IngredientJson } from "types/Material";
import { IDisplayQuantity, MeasurementUnit, MeasurementUnitList, displayNameForUnitOfMeasure } from "types/MeasurementUnit";
import { FMSError } from "types/Error";
import { Ingredients } from "components/receipeModal/ReceipeModal";

import {
  bulkUploadCateringRecipes,
  createCateringRecipe,
  deleteCateringRecipe,
  updateCateringRecipe,
} from "api/CateringRecipeApi";
import {
  invalidateCateringRecipesCache,
  useQueryCateringRecipesByBranch,
} from "hooks/CateringRecipes";
import { invalidateMaterialsCache } from "hooks/Materials";

import styles from "./CateringRecipeManagement.module.css";

interface IProps {
  branch: IBranch;
}

type IngredientOption =
  | { kind: "raw"; id: string; name: string }
  | { kind: "recipe"; id: string; name: string };

// Mirror the backend DisplayQuantity base→display conversion (g→kgs, ml→ltrs
// at/above 1000) so catering quantities render exactly like material recipes.
const toDisplayQuantity = (
  quantity: number,
  unit?: MeasurementUnit
): IDisplayQuantity => {
  if (quantity === 0 || Math.abs(quantity) >= 1000) {
    if (unit === MeasurementUnit.GRAMS) {
      return { unit: MeasurementUnit.KILOGRAMS, quantity: quantity / 1000 };
    }
    if (unit === MeasurementUnit.MILLI_LITRE) {
      return { unit: MeasurementUnit.LITRE, quantity: quantity / 1000 };
    }
  }
  return { unit: unit ?? MeasurementUnit.NOS, quantity };
};

// Build the same hierarchical ingredient tree the non-catering recipe modal
// renders: raw materials as leaves with display quantities, sub-recipes as
// collapsible nodes, unknown ids kept as labelled rows instead of crashing.
const buildCateringIngredientTree = (
  recipe: ICateringRecipe,
  allMaterials: Map<string, IMaterial> | undefined,
  recipesById: Map<string, ICateringRecipe>,
  visited: Set<string> = new Set([recipe.id])
): IngredientJson => {
  const tree: IngredientJson = {};
  for (const [id, qty] of Object.entries(recipe.ingredients || {})) {
    if (visited.has(id)) continue;
    const mat = allMaterials?.get(id);
    if (mat) {
      tree[mat.name] = {
        ...mat,
        displayQuantity: toDisplayQuantity(Number(qty), mat.unitOfMeasure),
        ingredients: undefined,
      } as unknown as IMaterial;
      continue;
    }
    const sub = recipesById.get(id);
    if (sub && visited.size < 6) {
      const next = new Set(visited);
      next.add(id);
      const children = buildCateringIngredientTree(sub, allMaterials, recipesById, next);
      tree[sub.name] = {
        id: sub.id,
        name: sub.name,
        unitOfMeasure: sub.unitOfMeasure,
        displayQuantity: toDisplayQuantity(
          Number(sub.yieldQuantity ?? 0),
          sub.unitOfMeasure
        ),
        ingredients:
          Object.keys(children).length > 0 ? children : undefined,
      } as unknown as IMaterial;
      continue;
    }
    const label = `Missing (${id.slice(0, 6)}…)`;
    tree[label] = {
      id,
      name: label,
      displayQuantity: undefined,
      ingredients: undefined,
    } as unknown as IMaterial;
  }
  return tree;
};

// ---- Container ----------------------------------------------------------

export const CateringRecipeManagement: React.FC<IProps> = ({ branch }) => {
  const businessUnit = branch.businessUnit as BusniessUnit | undefined;
  const { recipes, isFetching } = useQueryCateringRecipesByBranch(branch.id, businessUnit);
  const materialContext = useContext(MaterialContext);

  const [selected, setSelected] = useState<ICateringRecipe | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [toast, setToast] = useState<IToastBasicProps>({ open: false, message: "", type: "success" });

  // Build the ingredient option pool: raw materials visible in this branch + every
  // catering recipe in this BU.
  const ingredientOptions: IngredientOption[] = useMemo(() => {
    const opts: IngredientOption[] = [];
    const branchMaterials = materialContext.branchWiseMaterialsMap.get(branch.id);
    if (branchMaterials) {
      for (const m of branchMaterials.rawMaterialsArrForFoodAnalysis) {
        opts.push({ kind: "raw", id: m.id, name: m.name });
      }
    }
    for (const r of recipes) {
      opts.push({ kind: "recipe", id: r.id, name: r.name });
    }
    opts.sort((a, b) => a.name.localeCompare(b.name));
    return opts;
  }, [branch.id, recipes, materialContext]);

  const ingredientLookup: Map<string, IngredientOption> = useMemo(() => {
    const m = new Map<string, IngredientOption>();
    for (const o of ingredientOptions) m.set(o.id, o);
    return m;
  }, [ingredientOptions]);

  const columns = useMemo(
    () => [
      {
        Header: "Branch Table",
        columns: [
          {
            Header: "Item",
            accessor: "name",
            width: 624,
            Cell: ({ row }: any) => {
              const r = row.original as ICateringRecipe;
              return (
                <Button
                  style={{ display: "block", textAlign: "left", textTransform: "capitalize" }}
                  onClick={() => setSelected(r)}
                >
                  {r.name}
                </Button>
              );
            },
            disableFilters: true,
          },
          {
            Header: "Type",
            accessor: (r: ICateringRecipe) =>
              r.type === CateringRecipeType.MENU_ITEM ? "Menu Item" : "Sub Recipe",
            width: 160,
            disableGlobalFilter: true,
            disableFilters: true,
          },
          {
            Header: "Yield",
            accessor: (r: ICateringRecipe) =>
              `${r.yieldQuantity} ${displayNameForUnitOfMeasure(r.unitOfMeasure)}`,
            width: 112,
            disableGlobalFilter: true,
            disableFilters: true,
          },
        ],
      },
    ],
    []
  );

  const sortedRecipes = useMemo(
    () => [...recipes].sort((a, b) => a.name.localeCompare(b.name)),
    [recipes]
  );

  const recipesById = useMemo(
    () => new Map(recipes.map((r) => [r.id, r] as const)),
    [recipes]
  );

  const selectedTree = useMemo(
    () =>
      selected
        ? buildCateringIngredientTree(
            selected,
            materialContext.branchWiseMaterialsMap.get(branch.id)?.allMaterialsMap,
            recipesById
          )
        : {},
    [selected, recipesById, materialContext, branch.id]
  );

  return (
    <>
      <div className={styles.rightActions}>
        {Auth.getInstance().isAdmin() && (
          <>
            <Button variant="contained" onClick={() => setAddOpen(true)}>
              Add Recipe
            </Button>
            <Button variant="outlined" onClick={() => setUploadOpen(true)}>
              Bulk Upload
            </Button>
          </>
        )}
      </div>

      <DataTable
        columns={columns}
        data={sortedRecipes}
        tableClass={styles.table}
        searchable={true}
        isLoading={isFetching}
        tableHeight={600}
        initialSortBy="name"
      />

      {selected && (
        <ViewRecipeDialog
          recipe={selected}
          ingredientTree={selectedTree}
          ingredientLookup={ingredientLookup}
          ingredientOptions={ingredientOptions}
          allowEdit={Auth.getInstance().isAdmin()}
          onClose={(msg) => {
            setSelected(null);
            if (msg) setToast({ open: true, message: msg, type: "success" });
          }}
        />
      )}

      {addOpen && businessUnit && (
        <RecipeFormDialog
          mode="add"
          businessUnit={businessUnit}
          ingredientOptions={ingredientOptions}
          ingredientLookup={ingredientLookup}
          onClose={(msg) => {
            setAddOpen(false);
            if (msg) setToast({ open: true, message: msg, type: "success" });
          }}
        />
      )}

      {uploadOpen && (
        <BulkUploadDialog
          branchId={branch.id}
          onClose={(msg, isError) => {
            setUploadOpen(false);
            if (msg) setToast({ open: true, message: msg, type: isError ? "error" : "success" });
          }}
        />
      )}

      <Toast
        message={toast.message}
        open={toast.open}
        onClose={() => setToast({ ...toast, open: false })}
        type={toast.type}
      />
    </>
  );
};

// ---- View dialog --------------------------------------------------------

const ViewRecipeDialog: React.FC<{
  recipe: ICateringRecipe;
  ingredientTree: IngredientJson;
  ingredientLookup: Map<string, IngredientOption>;
  ingredientOptions: IngredientOption[];
  allowEdit: boolean;
  onClose: (successMessage?: string) => void;
}> = ({ recipe, ingredientTree, ingredientLookup, ingredientOptions, allowEdit, onClose }) => {
  const { primaryColor, primaryTextColor } = useContext(FMSThemeContext);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  if (editing) {
    return (
      <RecipeFormDialog
        mode="edit"
        recipe={recipe}
        businessUnit={recipe.businessUnit}
        ingredientOptions={ingredientOptions}
        ingredientLookup={ingredientLookup}
        onClose={(msg) => {
          if (msg) {
            onClose(msg);
          } else {
            setEditing(false);
          }
        }}
      />
    );
  }

  const handleDelete = async () => {
    if (!window.confirm(`Delete recipe "${recipe.name}"?`)) return;
    setDeleting(true);
    const err = await deleteCateringRecipe(recipe.id);
    setDeleting(false);
    if (err) {
      window.alert(err.message);
      return;
    }
    invalidateCateringRecipesCache();
    onClose("Recipe deleted");
  };

  const rows = Object.keys(ingredientTree || {});

  return (
    <Dialog open={true} maxWidth="md" onClose={(_, r) => r !== "backdropClick" && onClose()}>
      <div className={styles.viewModal}>
        <div className={styles.viewHeader} style={{ backgroundColor: primaryColor }}>
          <div className={styles.viewTitle} style={{ color: primaryTextColor }}>
            {recipe.name}
            <div className={styles.viewYield}>
              {recipe.yieldQuantity} {displayNameForUnitOfMeasure(recipe.unitOfMeasure)} ·{" "}
              {recipe.type === CateringRecipeType.MENU_ITEM ? "Menu Item" : "Sub Recipe"}
            </div>
          </div>
          <div className={styles.viewClose}>
            <IconButton color="inherit" onClick={() => onClose()} size="large">
              <CloseRoundedIcon />
            </IconButton>
          </div>
        </div>
        <div className={styles.viewBody}>
          {rows.length === 0 ? (
            <div className={styles.empty}>No ingredients configured.</div>
          ) : (
            <Ingredients ingredients={ingredientTree} />
          )}
        </div>
        <div className={styles.viewActions}>
          {allowEdit && (
            <>
              <Button onClick={() => setEditing(true)}>Edit</Button>
              <Button color="error" disabled={deleting} onClick={handleDelete}>
                {deleting ? "Deleting…" : "Delete"}
              </Button>
            </>
          )}
          <Button onClick={() => onClose()}>Close</Button>
        </div>
      </div>
    </Dialog>
  );
};

// ---- Add / Edit dialog --------------------------------------------------

interface IFormRow {
  optionId: string;
  quantity: string;
}

const RecipeFormDialog: React.FC<{
  mode: "add" | "edit";
  recipe?: ICateringRecipe;
  businessUnit: BusniessUnit;
  ingredientOptions: IngredientOption[];
  ingredientLookup: Map<string, IngredientOption>;
  onClose: (successMessage?: string) => void;
}> = ({ mode, recipe, businessUnit, ingredientOptions, ingredientLookup, onClose }) => {
  const [name, setName] = useState(recipe?.name ?? "");
  const [type, setType] = useState<CateringRecipeType>(recipe?.type ?? CateringRecipeType.MENU_ITEM);
  const [unit, setUnit] = useState<MeasurementUnit | "">(recipe?.unitOfMeasure ?? "");
  const [yieldQty, setYieldQty] = useState(recipe?.yieldQuantity?.toString() ?? "");
  const [rows, setRows] = useState<IFormRow[]>(() => {
    if (recipe?.ingredients && Object.keys(recipe.ingredients).length > 0) {
      return Object.entries(recipe.ingredients).map(([id, qty]) => ({
        optionId: id,
        quantity: String(qty),
      }));
    }
    return [{ optionId: "", quantity: "" }, { optionId: "", quantity: "" }];
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>("");

  const updateRow = (idx: number, patch: Partial<IFormRow>) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const handleSubmit = async () => {
    setError("");
    if (!name.trim() || !unit || !yieldQty) {
      setError("Name, yield quantity and unit are required");
      return;
    }
    const yieldNum = Number(yieldQty);
    if (!Number.isFinite(yieldNum) || yieldNum <= 0) {
      setError("Yield quantity must be > 0");
      return;
    }
    const ingredients: Record<string, number> = {};
    for (const r of rows) {
      if (!r.optionId) continue;
      const qty = Number(r.quantity);
      if (!Number.isFinite(qty) || qty <= 0) continue;
      ingredients[r.optionId] = (ingredients[r.optionId] || 0) + qty;
    }
    if (Object.keys(ingredients).length === 0) {
      setError("At least one ingredient with a positive quantity is required");
      return;
    }

    setSubmitting(true);
    const payload: ICateringRecipe = {
      id: recipe?.id ?? "",
      name: name.trim(),
      businessUnit,
      type,
      unitOfMeasure: unit as MeasurementUnit,
      yieldQuantity: yieldNum,
      ingredients,
      creationTime: recipe?.creationTime ?? Date.now(),
    };
    const result =
      mode === "add"
        ? await createCateringRecipe(payload)
        : await updateCateringRecipe(recipe!.id, payload);
    setSubmitting(false);
    if (result instanceof FMSError) {
      setError(result.message);
      return;
    }
    invalidateCateringRecipesCache();
    onClose(mode === "add" ? "Recipe added" : "Recipe updated");
  };

  return (
    <Dialog open={true} maxWidth="md" fullWidth onClose={(_, r) => r !== "backdropClick" && onClose()}>
      <div className={styles.formModal}>
        <div className={styles.formHeader}>
          {mode === "add" ? "Add Catering Recipe" : "Edit Catering Recipe"}
        </div>
        <div className={styles.formBody}>
          <div className={styles.formRow}>
            <TextField
              label="Name"
              variant="outlined"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
            />
          </div>
          <div className={styles.formRow}>
            <TextField
              label="Yield Quantity"
              variant="outlined"
              type="number"
              inputProps={{ step: "0.01" }}
              value={yieldQty}
              onChange={(e) => setYieldQty(e.target.value)}
              style={{ marginRight: 8 }}
              fullWidth
            />
            <Autocomplete
              options={MeasurementUnitList}
              value={unit || null}
              onChange={(_, v) => setUnit((v as MeasurementUnit) ?? "")}
              renderInput={(params) => <TextField {...params} label="Unit" variant="outlined" />}
              fullWidth
            />
          </div>
          <div className={styles.formRow}>
            <Autocomplete
              options={[CateringRecipeType.MENU_ITEM, CateringRecipeType.SUB_RECIPE]}
              getOptionLabel={(t) => (t === CateringRecipeType.MENU_ITEM ? "Menu Item" : "Sub Recipe")}
              value={type}
              onChange={(_, v) => v && setType(v)}
              renderInput={(params) => <TextField {...params} label="Type" variant="outlined" />}
              fullWidth
            />
          </div>

          <div className={styles.ingredientsHeader}>Ingredients</div>
          {rows.map((row, idx) => {
            const selectedOption = ingredientLookup.get(row.optionId) ?? null;
            return (
              <div key={idx} className={styles.ingredientRow}>
                <div className={styles.rowIndex}>{idx + 1}.</div>
                <Autocomplete
                  options={ingredientOptions}
                  getOptionLabel={(o) => `${o.name}${o.kind === "recipe" ? " (sub)" : ""}`}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  value={selectedOption}
                  onChange={(_, v) => updateRow(idx, { optionId: v?.id ?? "" })}
                  renderInput={(params) => <TextField {...params} label="Ingredient" variant="outlined" />}
                  style={{ flex: 1, marginRight: 8 }}
                />
                <TextField
                  label="Qty"
                  variant="outlined"
                  type="number"
                  inputProps={{ step: "0.01" }}
                  value={row.quantity}
                  onChange={(e) => updateRow(idx, { quantity: e.target.value })}
                  style={{ width: 120 }}
                />
                <Button onClick={() => setRows((prev) => prev.filter((_, i) => i !== idx))}>
                  ×
                </Button>
              </div>
            );
          })}
          <Button onClick={() => setRows([...rows, { optionId: "", quantity: "" }])}>
            + Add ingredient
          </Button>

          {error && <div className={styles.error}>{error}</div>}
        </div>
        <div className={styles.formActions}>
          <Button onClick={() => onClose()} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
            {submitting ? <Loading isLoading={true} /> : mode === "add" ? "Add" : "Save"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

// ---- Bulk upload --------------------------------------------------------

const BulkUploadDialog: React.FC<{
  branchId: string;
  onClose: (msg?: string, isError?: boolean) => void;
}> = ({ branchId, onClose }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string>("");

  const onDrop = async (files: File[]) => {
    if (files.length === 0) return;
    setUploading(true);
    setError("");
    const result = await bulkUploadCateringRecipes(branchId, files);
    setUploading(false);
    if (result) {
      setError(result.message);
      return;
    }
    invalidateCateringRecipesCache();
    invalidateMaterialsCache();
    onClose("Recipes uploaded", false);
  };

  const { getRootProps, getInputProps } = useDropzone({ onDrop, multiple: false });

  return (
    <Dialog open={true} maxWidth="sm" fullWidth onClose={(_, r) => r !== "backdropClick" && onClose()}>
      <div className={styles.formModal}>
        <div className={styles.formHeader}>Bulk Upload Catering Recipes</div>
        <div className={styles.formBody}>
          <div className={styles.dropzone} {...getRootProps()}>
            <input {...getInputProps()} />
            {uploading
              ? "Uploading…"
              : "Drop a recipe xlsx here, or click to choose a file"}
          </div>
          <div className={styles.uploadHint}>
            Columns: blank | Recipe Name | Type (MENU_ITEM/SUB_RECIPE) | Yield Qty |
            Yield Unit | Ingredient Name | Ingredient Qty
          </div>
          {error && <div className={styles.error}>{error}</div>}
        </div>
        <div className={styles.formActions}>
          <Button onClick={() => onClose()} disabled={uploading}>Close</Button>
        </div>
      </div>
    </Dialog>
  );
};
